<template>
  <div class="space-y-6">
    <!-- Filtros -->
    <div class="card">
      <h3 class="text-lg font-semibold mb-4" style="color: var(--text-primary)">🔍 {{ t('financeiro.relatorios.filters') }}</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1" style="color: var(--text-secondary)">{{ t('financeiro.relatorios.startDate') }}</label>
          <input v-model="filtros.dataInicio" type="date" class="input-field w-full" />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1" style="color: var(--text-secondary)">{{ t('financeiro.relatorios.endDate') }}</label>
          <input v-model="filtros.dataFim" type="date" class="input-field w-full" />
        </div>
        <div class="flex items-end">
          <button @click="aplicarFiltros" class="btn-primary w-full">{{ t('financeiro.relatorios.applyFilters') }}</button>
        </div>
      </div>
    </div>

    <!-- Fluxo de Caixa -->
    <div class="card">
      <h3 class="text-xl font-semibold mb-4" style="color: var(--text-primary)">💰 {{ t('financeiro.relatorios.cashFlow') }}</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="p-4 rounded-lg" style="background-color: var(--bg-primary)">
          <p class="text-sm mb-2" style="color: var(--text-secondary)">✅ {{ t('financeiro.relatorios.totalReceived') }}</p>
          <MoneyAmount :value="fluxoCaixa.totalRecebido || 0" tag="p" size="lg" class="text-3xl font-bold text-green-600" />
        </div>
        <div class="p-4 rounded-lg" style="background-color: var(--bg-primary)">
          <p class="text-sm mb-2" style="color: var(--text-secondary)">⏳ {{ t('financeiro.dashboard.pending') }}</p>
          <MoneyAmount :value="fluxoCaixa.totalPendente || 0" tag="p" size="lg" class="text-3xl font-bold text-orange-600" />
        </div>
        <div class="p-4 rounded-lg" style="background-color: var(--bg-primary)">
          <p class="text-sm mb-2" style="color: var(--text-secondary)">⚠️ {{ t('financeiro.relatorios.overdue') }}</p>
          <MoneyAmount :value="fluxoCaixa.totalAtrasado || 0" tag="p" size="lg" class="text-3xl font-bold text-red-600" />
        </div>
      </div>
      <div class="mt-4 text-sm" style="color: var(--text-secondary)">
        {{ t('financeiro.relatorios.period', { start: formatDate(fluxoCaixa.dataInicio), end: formatDate(fluxoCaixa.dataFim) }) }}
      </div>
    </div>

    <!-- Previsão de Recebíveis -->
    <div class="card section-card">
      <div class="section-header">
        <div class="section-title">
          <span class="section-icon">📈</span>
          <div>
            <h3 class="text-xl font-semibold" style="color: var(--text-primary)">{{ t('financeiro.relatorios.receivablesForecast') }}</h3>
            <p class="text-sm" style="color: var(--text-secondary)">{{ t('financeiro.relatorios.receivablesSubtitle') }}</p>
          </div>
        </div>
      </div>
      
      <div class="forecast-grid">
        <div class="metric-card metric-card-primary">
          <div class="metric-header">
            <span class="metric-icon">📅</span>
            <span class="metric-label">{{ t('financeiro.relatorios.currentMonth') }}</span>
          </div>
          <MoneyAmount :value="previsao.mesAtual?.totalPrevisto || 0" class="metric-value" size="lg" />
          <div class="metric-muted">{{ getMesNome(previsao.mesAtual?.mes) }} {{ previsao.mesAtual?.ano }}</div>
        </div>

        <div class="metric-card metric-card-secondary">
          <div class="metric-header">
            <span class="metric-icon">📆</span>
            <span class="metric-label">{{ t('financeiro.relatorios.nextMonth') }}</span>
          </div>
          <MoneyAmount :value="previsao.proximoMes?.totalPrevisto || 0" class="metric-value" size="lg" />
          <div class="metric-muted">{{ getMesNome(previsao.proximoMes?.mes) }} {{ previsao.proximoMes?.ano }}</div>
        </div>
      </div>

      <div class="forecast-panel">
        <div class="forecast-panel-header">
          <div class="forecast-panel-title">
            <span class="forecast-icon">🔮</span>
            <span class="font-semibold" style="color: var(--text-primary)">{{ t('financeiro.relatorios.nextMonths') }}</span>
          </div>
          <select v-model="qtdMesesPrevisao" @change="carregarDados" class="forecast-select">
            <option value="3">{{ t('financeiro.relatorios.months3') }}</option>
            <option value="6">{{ t('financeiro.relatorios.months6') }}</option>
            <option value="12">{{ t('financeiro.relatorios.months12') }}</option>
            <option value="24">{{ t('financeiro.relatorios.months24') }}</option>
          </select>
        </div>
        
        <div class="forecast-months">
          <div v-for="(mes, index) in getMesesPrevisao()" :key="index" class="forecast-month-card">
            <p class="forecast-month-label">{{ getMesNome(mes.mes) }}/{{ mes.ano }}</p>
            <MoneyAmount :value="mes.totalPrevisto || 0" tag="p" class="forecast-month-value" />
          </div>
        </div>
        
        <div class="forecast-total">
          <span class="font-semibold" style="color: var(--text-primary)">{{ t('financeiro.relatorios.totalForecast', { count: qtdMesesPrevisao }) }}</span>
          <MoneyAmount :value="previsao.proximosMeses?.total || 0" class="forecast-total-value" />
        </div>
      </div>
    </div>

    <!-- Pagamentos por Status -->
    <div class="card section-card">
      <div class="section-header">
        <div class="section-title">
          <span class="section-icon">📊</span>
          <div>
            <h3 class="text-xl font-semibold" style="color: var(--text-primary)">{{ t('financeiro.relatorios.paymentStatus') }}</h3>
            <p class="text-sm" style="color: var(--text-secondary)">{{ t('financeiro.relatorios.paymentStatusSubtitle') }}</p>
          </div>
        </div>
      </div>
      <div class="status-grid">
        <div class="status-card status-pendente">
          <div class="status-info">
            <p class="status-label">{{ t('financeiro.relatorios.pending') }}</p>
            <p class="status-value">{{ statusCount.pendentes || 0 }}</p>
          </div>
          <div class="status-icon">⏳</div>
        </div>

        <div class="status-card status-pago">
          <div class="status-info">
            <p class="status-label">{{ t('financeiro.relatorios.paid') }}</p>
            <p class="status-value">{{ statusCount.pagos || 0 }}</p>
          </div>
          <div class="status-icon">✅</div>
        </div>

        <div class="status-card status-atraso">
          <div class="status-info">
            <p class="status-label">{{ t('financeiro.relatorios.overduePlural') }}</p>
            <p class="status-value">{{ statusCount.atrasados || 0 }}</p>
          </div>
          <div class="status-icon">⚠️</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { intlLocaleFor } from '../../utils/format'
import MoneyAmount from '../MoneyAmount.vue'
import api from '../../services/api'
import Swal from 'sweetalert2'

const { t, locale } = useI18n()

const fluxoCaixa = ref({})
const previsao = ref({})
const statusCount = ref({})
const qtdMesesPrevisao = ref(12) // Padrão 12 meses

const formatDateInput = (date) => date.toISOString().split('T')[0]
const addDays = (date, days) => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

const today = new Date()

const filtros = ref({
  dataInicio: formatDateInput(addDays(today, -7)),
  dataFim: formatDateInput(addDays(today, 21))
})

onMounted(() => {
  carregarDados()
})

async function carregarDados() {
  try {
    const [fluxoRes, previsaoRes] = await Promise.all([
      api.get('/financeiro/relatorios/fluxo-caixa', { params: filtros.value }),
      api.get('/financeiro/relatorios/previsao-recebiveis', { params: { meses: qtdMesesPrevisao.value } })
    ])
    
    fluxoCaixa.value = fluxoRes.data
    previsao.value = previsaoRes.data
    
    await carregarStatusCount()
  } catch (error) {
    console.error('Erro ao carregar dados:', error)
    Swal.fire('Erro', t('financeiro.relatorios.loadError'), 'error')
  }
}

function getMesesPrevisao() {
  const meses = []
  const proximosMeses = previsao.value.proximosMeses
  
  if (!proximosMeses) return meses
  
  const qtd = proximosMeses.quantidadeMeses || 0
  
  for (let i = 1; i <= qtd; i++) {
    const mes = proximosMeses[`mes${i}`]
    if (mes) {
      meses.push(mes)
    }
  }
  
  return meses
}

async function carregarStatusCount() {
  try {
    const [pendentesRes, pagosRes, atrasadosRes] = await Promise.all([
      api.get('/financeiro/pagamentos', { params: { dataInicio: filtros.value.dataInicio, dataFim: filtros.value.dataFim } }),
      api.get('/financeiro/pagamentos', { params: { dataInicio: filtros.value.dataInicio, dataFim: filtros.value.dataFim } }),
      api.get('/financeiro/pagamentos/atrasados')
    ])
    
    const todosRes = pendentesRes.data
    statusCount.value = {
      pendentes: todosRes.filter(p => p.status === 'PENDENTE').length,
      pagos: todosRes.filter(p => p.status === 'PAGO').length,
      atrasados: atrasadosRes.data.length
    }
  } catch (error) {
    console.error('Erro ao carregar contadores:', error)
  }
}

function aplicarFiltros() {
  carregarDados()
}

function formatDate(date) {
  if (!date) return '-'
  return new Date(date).toLocaleDateString(intlLocaleFor(locale.value))
}

function getMesNome(mes) {
  const keys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dec']
  const key = keys[mes - 1]
  return key ? t(`financeiro.relatorios.months.${key}`) : '-'
}
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 rounded-lg font-medium transition-all bg-blue-600 text-white hover:bg-blue-700;
}

.section-card {
  position: relative;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(99, 102, 241, 0.2));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.forecast-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.metric-card {
  padding: 1rem 1.25rem;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: linear-gradient(180deg, var(--bg-primary), var(--bg-secondary));
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.metric-card-primary {
  border-color: rgba(59, 130, 246, 0.35);
}

.metric-card-secondary {
  border-color: rgba(16, 185, 129, 0.35);
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.metric-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background-color: rgba(59, 130, 246, 0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.metric-label {
  font-weight: 600;
  color: var(--text-primary);
}

.metric-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text-primary);
}

.metric-muted {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

.forecast-panel {
  padding: 1rem 1.25rem;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: var(--bg-primary);
}

.forecast-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.forecast-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.forecast-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background-color: rgba(99, 102, 241, 0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.forecast-select {
  padding: 0.35rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.8rem;
}

.forecast-months {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.forecast-month-card {
  padding: 0.7rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  background-color: var(--bg-secondary);
}

.forecast-month-label {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.2rem;
}

.forecast-month-value {
  font-weight: 700;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.forecast-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--border);
  padding-top: 0.75rem;
}

.forecast-total-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #16a34a;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.status-card {
  padding: 1rem 1.25rem;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: linear-gradient(180deg, var(--bg-primary), var(--bg-secondary));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.status-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-weight: 600;
}

.status-value {
  font-size: 2rem;
  font-weight: 700;
}

.status-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  opacity: 0.8;
}

.status-pendente .status-value {
  color: #f97316;
}

.status-pendente .status-icon {
  background: rgba(249, 115, 22, 0.15);
}

.status-pago .status-value {
  color: #16a34a;
}

.status-pago .status-icon {
  background: rgba(22, 163, 74, 0.15);
}

.status-atraso .status-value {
  color: #dc2626;
}

.status-atraso .status-icon {
  background: rgba(220, 38, 38, 0.15);
}

@media (max-width: 1024px) {
  .forecast-grid,
  .status-grid {
    grid-template-columns: 1fr;
  }
}

.input-field {
  @apply px-3 py-2 rounded-lg border;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--border);
}

.card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
}
</style>

